import { useEffect, useState } from "react";
import {
  Banner,
  Body,
  Box,
  Button,
  Checkbox,
  ContentRow,
  ErrorBanner,
  Loader,
  LocalizedString,
  Space,
  Stack,
  unsafeLocalizedString,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import { Contracts, ReadingConfirmProps } from "./Contracts";
import { array, eq, nonEmptyArray, option, set, taskEither } from "fp-ts";
import { constant, constNull, constTrue, flow, pipe } from "fp-ts/function";

import * as remoteData from "../../RemoteData";
import {
  dismissMarketingModalAction,
  reducerConfig,
  startSignatureAction,
} from "./state";
import { MarketingConsentConfirmationDialog } from "./MarketingConsentConfirmationDialog";
import { NonEmptyString } from "io-ts-types/lib/NonEmptyString";
import { MarketingConsentDialog } from "./MarketingConsentDialog";
import { ReaderTaskEither } from "fp-ts/ReaderTaskEither";
import { TaskEither } from "fp-ts/TaskEither";
import { useRemoteData } from "../../useRemoteData";
import { Option } from "fp-ts/Option";
import * as api from "./api";
import { useParentSharedReducer } from "../../BranchExperience/useSharedReducer";
import { useBranchExperienceContext } from "../../BranchExperience/BranchExperienceContext";
import { DocumentsSignature, SignatureProps } from "./DocumentsSignature";
import {
  getPreContractualDocumentsActions,
  getSmartKeyInitialMode,
  useFormatError,
  useTermDepositFormatError,
} from "./utils";
import { LocaleKey, RuntimeLocaleKey, useFormatMessage } from "../../intl";
import { TermsAndConditionsG4P } from "./TermsAndConditionsG4P";
import { CheckingRemoteClientSignature } from "../../StandardLoan/FollowUpAndSignature/LowRiskClient/CheckingRemoteClientSignature";
import { LFStatus } from "../../StandardLoan/api";
import { usePortalStatusContext } from "../../PortalStatusContext";
import { useCommand } from "../../useAPI";
import { ContentWrapper } from "../ContentWrapper";
import { usePersistentFocusRestore } from "../usePersistentFocus";
import { DocSignSignPage } from "./api";

interface Props {
  onContinue: () => unknown;
  onContinueRemoteSigning: (status: Option<LFStatus>) => unknown;
  onReadContract: ReaderTaskEither<NonEmptyString, unknown, unknown>;
  onUnreadContract: ReaderTaskEither<NonEmptyString, unknown, unknown>;
  onTermsAndConditionsReceived: TaskEither<unknown, unknown>;
  onAdequateExplanationConsent: ReaderTaskEither<boolean, unknown, unknown>;
  onMarketingConsentExpressed: ReaderTaskEither<boolean, unknown, unknown>;
  getDocuments: TaskEither<unknown, api.PreContractualDocumentsListOutput>;
  onBack: () => unknown;
  getTermsAndConditions: Option<
    TaskEither<unknown, api.TermsAndConditionsOutput>
  >;
  showReadingConfirm: boolean;
  showMarketingConsent: boolean;
  showContractsAgreement: boolean;
  showAdequateExplanationConsent: boolean;
  inlineReadingCheckbox: boolean;
  promotion: Option<LocalizedString>;
  signatureProps: SignatureProps;
  hasInsurance?: boolean;
  mustReadAllDocuments?: boolean;
  skipContracts: boolean;
  showBackButton: boolean;
  hideDocumentsSignatureBox?: boolean;
  startCheckingRemoteSignature?: boolean;
  shouldVerifySignatureStatus?: boolean;
  onAfterSignature?: () => unknown;
  isForeignClient?: boolean;
  onForeignSigningConsentExpressed?: ReaderTaskEither<
    boolean,
    unknown,
    unknown
  >;
  variant?: string;
  attemptedToSign?: boolean;
  setAttemptedToSign?: (value: boolean) => void;
  label?: LocaleKey;
}

export function PreContractualDocuments(props: Props) {
  const formatMessage = useFormatMessage();
  const formatError = useFormatError();
  const formatErrorTermDeposit = useTermDepositFormatError();
  const isMobileLayout = useIsMobileLayout();
  const { portalBlocked } = usePortalStatusContext();
  const [docSignSignPage, setDocSignSignPage] = useState<DocSignSignPage>();
  const fetchDocSignSignPage = useCommand(api.getDocSignSignPage);
  const [
    preContractReceivedConfirmation,
    setPreContractReceivedConfirmation,
  ] = useState(false);
  const savePreContractReceivedConfirmation = useCommand(
    api.savePreContractReceivedConfirmation
  );
  const [additionalConfirmation, setAdditionalConfirmation] = useState(false);
  const saveAdditionalConfirmation = useCommand(api.saveAdditionalConfirmation);
  const { attemptedToSign, setAttemptedToSign } = props;

  useEffect(() => {
    pipe(
      fetchDocSignSignPage(),
      taskEither.fold(
        _ => taskEither.fromIO(() => {}),
        signPage =>
          taskEither.fromIO(() => {
            if (signPage) {
              setDocSignSignPage(signPage);
            }
          })
      )
    )();
  }, []);

  const [state, dispatch] = useParentSharedReducer(reducerConfig, {
    type: "ReadDocuments",
    documentsRead: set.empty,
    documentsReceivedConfirmation: !props.showContractsAgreement,
    explanationConsent: !props.showAdequateExplanationConsent,
    marketingConsent: false,
    isMarketingConsentModalOpen: false,
    error: option.none,
    authenticationMethod: option.some(
      props.signatureProps.authenticationMethod
    ),
    foreignSigningConsent: false,
  });

  usePersistentFocusRestore([state]);

  const [marketingModalOpen, setMarketingModalOpen] = useState(false);
  const preContractualDocuments = useRemoteData(props.getDocuments);
  const { branchExperienceFeaturesActive } = useBranchExperienceContext();

  const {
    handleContractRead,
    handleContractUnread,
    handlePreContractualReceived,
    handleExplanationConsentAcceptance,
    handleMarketingConsentAcceptance,
    handleModalMarketingConsentAcceptance,
    handleForeignSigningConsentAcceptance,
  } = getPreContractualDocumentsActions(dispatch, {
    contractRead: props.onReadContract,
    contractUnread: props.onUnreadContract,
    termsAndConditionsReceived: props.onTermsAndConditionsReceived,
    adequateExplanationConsent: props.onAdequateExplanationConsent,
    marketingConsent: props.onMarketingConsentExpressed,
    foreignSigningConsent: props.onForeignSigningConsentExpressed,
  });

  return pipe(
    preContractualDocuments,
    remoteData.fold(
      () => (
        <Box width="100%" hAlignContent="center">
          <Loader
            ariaLabelMessage={formatMessage(
              "generic.loading.alt" as RuntimeLocaleKey
            )}
          />
        </Box>
      ),
      constNull,
      flow(
        nonEmptyArray.filter(({ contractualDocument }) =>
          pipe(contractualDocument, option.getOrElse(constTrue))
        ),
        option.fold(constNull, documents => {
          const documentsRead =
            state.type === "ReadDocuments" && props.showReadingConfirm
              ? state.documentsRead
              : pipe(
                  documents,
                  nonEmptyArray.map(document => document.docId),
                  set.fromArray<NonEmptyString>(eq.eqString)
                );

          const contractsReadingConfirmationProps: ReadingConfirmProps = props.showReadingConfirm
            ? branchExperienceFeaturesActive
              ? {
                  readingConfirmationType: "status",
                  documentsRead,
                }
              : {
                  readingConfirmationType: "checkbox",
                  documentsRead,
                  onDocumentRead: handleContractRead,
                  onDocumentUnread: handleContractUnread,
                }
            : { readingConfirmationType: "none" };

          const explanationConsentCheckbox = (
            <Box shrink>
              <ContentWrapper mobileRowType={"panel-like-margins"}>
                <Checkbox
                  name="explanation-consent"
                  multiline
                  label={
                    props.variant === "TermDeposit"
                      ? option.some(
                          formatMessage(
                            "TermDepositAccount.ReadDocuments.explanationConsent"
                          )
                        )
                      : option.some(
                          formatMessage(
                            "UKonto.ReadDocuments.explanationConsent"
                          )
                        )
                  }
                  value={
                    state.type !== "ReadDocuments" || state.explanationConsent
                  }
                  onChange={event =>
                    handleExplanationConsentAcceptance(
                      event.currentTarget.checked
                    )()
                  }
                  issuesType={
                    state.type === "ReadDocuments"
                      ? pipe(
                          state.error,
                          option.filter(constant(!state.explanationConsent)),
                          option.map(constant("error" as const))
                        )
                      : option.none
                  }
                  disabled={
                    state.type === "SigningDocuments" ||
                    branchExperienceFeaturesActive ||
                    portalBlocked
                  }
                />
              </ContentWrapper>
            </Box>
          );

          const marketingConsentCheckbox = (
            <Box shrink>
              <ContentWrapper mobileRowType={"panel-like-margins"}>
                <Checkbox
                  name="marketing-consent"
                  multiline
                  label={option.some([
                    formatMessage("UKonto.ReadDocuments.marketingConsent"),
                  ])}
                  cta={{
                    label: formatMessage(
                      "UKonto.ReadDocuments.marketingConsentMoreInfo"
                    ),
                    action: () => setMarketingModalOpen(true),
                    ariaLabel: formatMessage("MarketingConsent.link.alt"),
                  }}
                  value={state.marketingConsent}
                  onChange={event =>
                    handleMarketingConsentAcceptance(
                      event.currentTarget.checked
                    )()
                  }
                  issuesType={option.none}
                  disabled={
                    state.type === "SigningDocuments" ||
                    branchExperienceFeaturesActive ||
                    portalBlocked
                  }
                />
              </ContentWrapper>
            </Box>
          );

          const documentsSignatureBox = (
            <DocumentsSignature
              startSignature={() =>
                dispatch(
                  startSignatureAction(
                    documents,
                    props.showReadingConfirm,
                    props.showMarketingConsent,
                    docSignSignPage?.preContractConfirmationNeeded
                      ? preContractReceivedConfirmation
                      : true,
                    docSignSignPage?.additionalConfirmationNeeded
                      ? additionalConfirmation
                      : true,
                    props.showAdequateExplanationConsent,
                    props.isForeignClient ? props.isForeignClient : false
                  )
                )
              }
              onContinue={
                props.shouldVerifySignatureStatus &&
                props.onAfterSignature !== undefined
                  ? props.onAfterSignature
                  : props.onContinue
              }
              isSignatureStarted={state.type === "SigningDocuments"}
              signatureProps={props.signatureProps}
              smartKeyInitialMode={pipe(
                getSmartKeyInitialMode(state.authenticationMethod),
                option.toUndefined
              )}
              onCancelSignature={() => dispatch({ type: "CancelSignature" })}
              otpLength={props.skipContracts ? 6 : undefined}
              hideRequestNotice={props.skipContracts}
              setAttemptedToSign={setAttemptedToSign}
            />
          );

          const triggerRemoteFinalPage = (
            <CheckingRemoteClientSignature
              onContinueRemoteSigning={props.onContinueRemoteSigning}
              onCancelSignature={() => dispatch({ type: "CancelSignature" })}
            />
          );

          const contractsAgreementCheckbox = (
            <Box shrink>
              <ContentWrapper mobileRowType={"panel-like-margins"}>
                <Checkbox
                  name="contracts-agreement"
                  multiline
                  label={
                    props.variant === "TermDeposit"
                      ? option.some(
                          formatMessage(
                            "TermDepositAccount.ReadDocuments.contractsAgreement"
                          )
                        )
                      : option.some(
                          formatMessage(
                            "UKonto.ReadDocuments.contractsAgreement"
                          )
                        )
                  }
                  value={
                    state.type !== "ReadDocuments" ||
                    state.documentsReceivedConfirmation
                  }
                  onChange={event =>
                    handlePreContractualReceived(event.currentTarget.checked)()
                  }
                  issuesType={
                    state.type === "ReadDocuments"
                      ? pipe(
                          state.error,
                          option.filter(
                            constant(!state.documentsReceivedConfirmation)
                          ),
                          option.map(constant("error" as const))
                        )
                      : option.none
                  }
                  disabled={
                    state.type === "SigningDocuments" ||
                    branchExperienceFeaturesActive ||
                    portalBlocked
                  }
                />
              </ContentWrapper>
            </Box>
          );

          const foreignSigningConsentCheckbox = (
            <Box shrink>
              <ContentWrapper mobileRowType={"panel-like-margins"}>
                <Checkbox
                  name="foreign-consent"
                  multiline
                  label={option.some(
                    formatMessage("UKonto.ReadDocuments.foreignSigningConsent")
                  )}
                  value={
                    state.type !== "ReadDocuments" ||
                    state.foreignSigningConsent
                  }
                  onChange={event =>
                    handleForeignSigningConsentAcceptance(
                      event.currentTarget.checked
                    )()
                  }
                  issuesType={
                    state.type === "ReadDocuments"
                      ? pipe(
                          state.error,
                          option.filter(constant(!state.foreignSigningConsent)),
                          option.map(constant("error" as const))
                        )
                      : option.none
                  }
                  disabled={
                    state.type === "SigningDocuments" ||
                    branchExperienceFeaturesActive ||
                    portalBlocked
                  }
                />
              </ContentWrapper>
            </Box>
          );

          return (
            <Box column grow shrink>
              {!!docSignSignPage?.expirationDateTime && (
                <>
                  <Body size={"medium"} weight={"regular"}>
                    {unsafeLocalizedString(
                      formatMessage("DocSign.SignPage.ValidUntil.label") +
                        ": " +
                        docSignSignPage.expirationDateTime
                    )}
                  </Body>
                  <Space units={4} />
                </>
              )}
              {!props.skipContracts && (
                <Contracts
                  {...contractsReadingConfirmationProps}
                  hasInsurance={props.hasInsurance}
                  documents={documents}
                  showErrors={
                    state.type === "ReadDocuments" && option.isSome(state.error)
                  }
                  mustReadAllDocuments={props.mustReadAllDocuments}
                  isSigningStarted={state.type === "SigningDocuments"}
                  variant={props.variant}
                  isForeignClient={props.isForeignClient}
                  label={props.label}
                />
              )}
              {pipe(
                props.promotion,
                option.map(promotion => (
                  <>
                    <Space units={12} />
                    <Banner
                      type="informative"
                      title={option.some(
                        formatMessage("CreateProfile.promotionTitle")
                      )}
                      content={promotion}
                      actions={option.none}
                      onDismiss={option.none}
                    />
                  </>
                )),
                option.toNullable
              )}
              {!props.skipContracts &&
                pipe(
                  props.getTermsAndConditions,
                  option.map(getTermsAndConditions => (
                    <>
                      <Space units={isMobileLayout ? 2 : 12} />
                      <TermsAndConditionsG4P
                        getTermsAndConditions={getTermsAndConditions}
                        variant={props.variant}
                      />
                    </>
                  )),
                  option.toNullable
                )}
              <Space units={isMobileLayout ? 2 : 12} />
              <Stack column units={2}>
                {props.showAdequateExplanationConsent &&
                  explanationConsentCheckbox}
                {props.showContractsAgreement && contractsAgreementCheckbox}
                {props.showMarketingConsent && marketingConsentCheckbox}
                {props.isForeignClient && foreignSigningConsentCheckbox}
              </Stack>
              {state.type === "ReadDocuments"
                ? pipe(
                    state.error,
                    option.map(
                      array.map(error => {
                        if (
                          error === "DocumentsUnread" ||
                          error === "ForeignSigningConsent"
                        ) {
                          return (
                            <>
                              <Space units={4} />
                              <ErrorBanner>
                                {props.variant === "TermDeposit"
                                  ? formatErrorTermDeposit(error)
                                  : formatError(error)}
                              </ErrorBanner>
                            </>
                          );
                        }
                        if (error === "PrecontractualConsent") {
                          return (
                            <>
                              <Space units={4} />
                              <ErrorBanner>
                                {props.variant === "TermDeposit"
                                  ? formatMessage(
                                      "TermDepositAccount.ReadDocuments.documentsReceivedConfirmationCheckError"
                                    )
                                  : formatMessage(
                                      "UKonto.ReadDocuments.documentsReceivedConfirmationCheckError"
                                    )}
                              </ErrorBanner>
                            </>
                          );
                        }
                        if (error === "ExplanationConsent") {
                          return (
                            <>
                              <Space units={4} />
                              <ErrorBanner>
                                {formatMessage(
                                  "UKonto.ReadDocuments.ExplanationConsent.CheckError"
                                )}
                              </ErrorBanner>
                            </>
                          );
                        }
                        return null;
                      })
                    ),
                    option.getOrElseW(constNull)
                  )
                : null}
              {docSignSignPage?.additionalConfirmationNeeded &&
                state.type === "ReadDocuments" && (
                  <>
                    <Space units={4} />
                    <Box shrink>
                      <ContentWrapper mobileRowType={"panel-like-margins"}>
                        <Checkbox
                          name="consent"
                          label={option.some(
                            formatMessage(
                              `DocSign.SignPage.Confirmation.Client.${docSignSignPage?.flowType}` as LocaleKey
                            )
                          )}
                          value={additionalConfirmation}
                          onChange={checked => {
                            if (checked.currentTarget.checked) {
                              setAdditionalConfirmation(true);
                              pipe(
                                saveAdditionalConfirmation(),
                                taskEither.fold(
                                  _ => taskEither.fromIO(() => {}),
                                  _ => taskEither.fromIO(() => {})
                                )
                              )();
                            } else {
                              setAdditionalConfirmation(false);
                            }
                          }}
                          issuesType={
                            !additionalConfirmation && attemptedToSign
                              ? option.some("error")
                              : option.none
                          }
                          disabled={false}
                        />
                      </ContentWrapper>
                    </Box>

                    {!additionalConfirmation && attemptedToSign && (
                      <>
                        <Space units={4} />
                        <ErrorBanner>
                          {formatMessage(
                            "DocSign.SignPage.Confirmation.Client.Banner.error"
                          )}
                        </ErrorBanner>
                      </>
                    )}
                  </>
                )}
              {docSignSignPage?.preContractConfirmationNeeded &&
                state.type === "ReadDocuments" && (
                  <>
                    <Space units={4} />
                    <Box shrink>
                      <ContentWrapper mobileRowType={"panel-like-margins"}>
                        <Checkbox
                          name="consent"
                          label={option.some(
                            formatMessage(
                              "DocSign.PreContractual.Confirmation.Client"
                            )
                          )}
                          value={preContractReceivedConfirmation}
                          onChange={checked => {
                            if (checked.currentTarget.checked) {
                              setPreContractReceivedConfirmation(true);
                              pipe(
                                savePreContractReceivedConfirmation(),
                                taskEither.fold(
                                  _ => taskEither.fromIO(() => {}),
                                  _ => taskEither.fromIO(() => {})
                                )
                              )();
                            } else {
                              setPreContractReceivedConfirmation(false);
                            }
                          }}
                          issuesType={
                            !preContractReceivedConfirmation && attemptedToSign
                              ? option.some("error")
                              : option.none
                          }
                          disabled={false}
                        />
                      </ContentWrapper>
                    </Box>

                    {!preContractReceivedConfirmation && attemptedToSign && (
                      <>
                        <Space units={4} />
                        <ErrorBanner>
                          {formatMessage(
                            "DocSign.PreContractual.Confirmation.Client.Banner.error"
                          )}
                        </ErrorBanner>
                      </>
                    )}
                  </>
                )}
              <Space units={8} />
              <Box
                column
                grow
                style={isMobileLayout ? { width: "100%" } : undefined}
              >
                <ContentWrapper mobileRowType={"panel-like-margins"}>
                  <Box
                    shrink
                    grow
                    vAlignContent="bottom"
                    hAlignContent="center"
                  >
                    {!props.hideDocumentsSignatureBox &&
                      props.skipContracts &&
                      props.signatureProps.authenticationMethod === "PIN" && (
                        <>
                          <Banner
                            type="informative"
                            content={formatMessage(
                              "SB.AuthorizeChanges.info.label"
                            )}
                            title={option.some(
                              formatMessage("SB.AuthorizeChanges.info.title")
                            )}
                            actions={option.none}
                            onDismiss={option.none}
                          />
                          {isMobileLayout && <Space units={2} />}
                        </>
                      )}
                    {!props.hideDocumentsSignatureBox && documentsSignatureBox}
                  </Box>
                </ContentWrapper>
                {props.signatureProps.authenticationMethod === "PIN" &&
                props.showBackButton ? (
                  <>
                    <Space units={6} />
                    <ContentRow
                      type={isMobileLayout ? "full" : "lateral-margins"}
                    >
                      <Box
                        shrink
                        grow
                        vAlignContent="top"
                        hAlignContent="center"
                      >
                        <Button
                          variant="text"
                          size="default"
                          label={formatMessage("Back")}
                          action={props.onBack}
                          disabled={
                            state.type === "SigningDocuments" || portalBlocked
                          }
                        />
                      </Box>
                    </ContentRow>
                    <Space units={3} />
                  </>
                ) : (
                  <Space units={12} />
                )}
              </Box>
              {props.startCheckingRemoteSignature && triggerRemoteFinalPage}
              {state.type === "ReadDocuments" &&
                state.isMarketingConsentModalOpen &&
                !branchExperienceFeaturesActive && (
                  <MarketingConsentConfirmationDialog
                    onAnswer={consent =>
                      handleModalMarketingConsentAcceptance(consent)()
                    }
                    onDismiss={flow(dismissMarketingModalAction, dispatch)}
                  />
                )}
              {marketingModalOpen && (
                <MarketingConsentDialog
                  onDismiss={() => setMarketingModalOpen(false)}
                />
              )}
            </Box>
          );
        })
      )
    )
  );
}
